/**
 * Undo and redo, as a stack of commands rather than a stack of documents.
 *
 * Keeping whole scores would be simpler and would cost the size of the piece for every
 * keystroke; keeping commands costs the size of the edit. The document is immutable, so
 * the two stacks and the score are just three fields, and a session transitions to a new
 * session the way a score transitions to a new score.
 *
 * A history entry always holds the command that moves the session *the other way*: in
 * {@link EditSession.past} that is the undo, in {@link EditSession.future} the redo. Redo
 * deliberately does not replay the original command. Replaying `insertMeasure` would mint
 * a fresh bar with a fresh id, so the bar that came back would not be the bar that went
 * away and every earlier entry naming it would be stale. Applying the inverse of the undo
 * puts the original id back, which is the only version of redo that leaves the history
 * still true.
 */

import type { Score } from "../document/types";
import { apply } from "./apply";
import type { Command } from "./types";

/** One step of history, holding the command that reverses it. */
export interface HistoryEntry {
  readonly label: string;
  readonly command: Command;
}

/** A score together with the way back to its earlier versions. */
export interface EditSession {
  readonly score: Score;
  /** Oldest first. Each entry's command undoes the edit it stands for. */
  readonly past: readonly HistoryEntry[];
  /** Nearest first. Each entry's command redoes the edit it stands for. */
  readonly future: readonly HistoryEntry[];
  /** How many undo steps are kept. Older ones fall off the bottom. */
  readonly limit: number;
}

/** The outcome of a session step: a new session, or the old one and a reason. */
export type SessionResult =
  | { readonly ok: true; readonly session: EditSession }
  | { readonly ok: false; readonly session: EditSession; readonly reason: string };

export const DEFAULT_HISTORY_LIMIT = 200;

/** A session over a score, with nothing to undo yet. */
export function createSession(score: Score, limit = DEFAULT_HISTORY_LIMIT): EditSession {
  return { score, past: [], future: [], limit: Math.max(1, limit) };
}

/**
 * Run a command and record it.
 *
 * A new edit drops the redo stack: once the piece has gone somewhere else, the steps that
 * led away from the old future no longer describe this document.
 */
export function run(session: EditSession, command: Command, label?: string): SessionResult {
  const result = apply(session.score, command);
  if (!result.ok) return { ok: false, session, reason: result.reason };

  const entry: HistoryEntry = { label: label ?? describe(command), command: result.inverse };
  const past = [...session.past, entry].slice(-session.limit);
  return { ok: true, session: { ...session, score: result.score, past, future: [] } };
}

/** Take the last edit back. */
export function undo(session: EditSession): SessionResult {
  const entry = session.past[session.past.length - 1];
  if (entry === undefined) return { ok: false, session, reason: "nothing to undo" };

  const result = apply(session.score, entry.command);
  if (!result.ok) return { ok: false, session, reason: result.reason };

  return {
    ok: true,
    session: {
      ...session,
      score: result.score,
      past: session.past.slice(0, -1),
      future: [{ label: entry.label, command: result.inverse }, ...session.future],
    },
  };
}

/** Put back the edit the last undo took away. */
export function redo(session: EditSession): SessionResult {
  const entry = session.future[0];
  if (entry === undefined) return { ok: false, session, reason: "nothing to redo" };

  const result = apply(session.score, entry.command);
  if (!result.ok) return { ok: false, session, reason: result.reason };

  return {
    ok: true,
    session: {
      ...session,
      score: result.score,
      past: [...session.past, { label: entry.label, command: result.inverse }].slice(
        -session.limit,
      ),
      future: session.future.slice(1),
    },
  };
}

export function canUndo(session: EditSession): boolean {
  return session.past.length > 0;
}

export function canRedo(session: EditSession): boolean {
  return session.future.length > 0;
}

/** What the next undo would take back, for a menu item. */
export function undoLabel(session: EditSession): string | null {
  return session.past[session.past.length - 1]?.label ?? null;
}

/** What the next redo would put back, for a menu item. */
export function redoLabel(session: EditSession): string | null {
  return session.future[0]?.label ?? null;
}

/** Forget the history without touching the score, e.g. after a save. */
export function clearHistory(session: EditSession): EditSession {
  return { ...session, past: [], future: [] };
}

const LABELS: Readonly<Record<Command["kind"], string>> = {
  setMeta: "Change score details",
  setLayout: "Change the page",
  setMeasure: "Change measure",
  insertMeasure: "Insert measure",
  removeMeasure: "Delete measure",
  restoreMeasure: "Restore measure",
  insertPart: "Add part",
  removePart: "Delete part",
  restorePart: "Restore part",
  setPart: "Change part",
  setGroups: "Change staff groups",
  setPartMeasure: "Change measure contents",
  setSpanners: "Change slurs and hairpins",
  insertEvent: "Insert note",
  removeEvent: "Delete note",
  replaceEvent: "Replace note",
  setDuration: "Change duration",
  setPitches: "Change pitch",
  setEventProps: "Change note marks",
  setClef: "Change clef",
  setInstrument: "Change instrument",
  addDirection: "Add direction",
  removeDirection: "Delete direction",
  addSpanner: "Add slur or hairpin",
  removeSpanner: "Delete slur or hairpin",
  batch: "Edit",
};

/** A human-readable name for a command, used when the caller supplies none. */
export function describe(command: Command): string {
  if (command.kind === "batch" && command.label !== undefined) return command.label;
  return LABELS[command.kind];
}
